import React, { useEffect, useState, useCallback } from 'react';
import { EmailProps, EmailApiResponse } from '@dreamai/types';
import WysiwygEditor from '../../../common/WysiwygEditor';
import IframeWithDynamicHeight from './IframeWithDynamicHeight';
import AttachmentItem from './AttachmentItem';
import EmailActions from './email-helpers/EmailActions';
import EmailMetadata from './email-helpers/EmailMetadata';
import { useEmailFormManagement } from './email-helpers/useEmailFormManagement';
import { useEmail } from '../../../../providers/EmailProvider';
import { useAuth0 } from '@auth0/auth0-react';
import {
  saveDraftEmail,
  sendDraftEmail,
  downloadAttachment,
} from './email-helpers/emailApiHelpers';
import GenericAIComponentChange from './GenericAIComponentChange';
import {
  detectFancyEmail,
  getEmailTypeClass,
} from './email-helpers/emailUtils';

import './email-component.scss';
import AIReportingWrapper from '../AIReportingWrapper';
import InboxViewComponentView from './InboxViewComponentView';
import ErrorState from '../../../../components/common/ErrorState';
import EmailShareModal from './email-helpers/EmailShareModal';
import api from '../../../../api';
import { ReactComponent as UpCaretIcon } from '../../../../icons/up-caret.svg';

// Component props interface that extends EmailProps with threadId
interface EmailComponentProps extends EmailProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  isInboxView?: boolean;
  onInboxViewComponentViewClick?: (componentId: string) => void;
  removeDedicatedSubmitButton?: boolean;
  isConversationView?: boolean;
  /** If true, the email will start in edit mode (for drafts) */
  defaultEditMode?: boolean;
  /** If provided, shows a collapse button in the header */
  onCollapse?: () => void;
  /** Called after successfully saving a draft */
  onSave?: () => void;
  /** Called after successfully sending an email */
  onSend?: () => void;
}

const EmailComponent: React.FC<EmailComponentProps> = ({
  emailId,
  threadId,
  updateThreadViewVisualDescription,
  isInboxView = false,
  onInboxViewComponentViewClick,
  removeDedicatedSubmitButton,
  isConversationView = false,
  defaultEditMode = false,
  onCollapse,
  onSave,
  onSend,
}) => {
  const { getAccessTokenSilently, user: auth0User } = useAuth0();
  const { getEmail, updateEmailOptimistically } = useEmail();
  const [isFancyEmail, setIsFancyEmail] = useState<boolean>(false);
  const [isExitingAiEdits, setIsExitingAiEdits] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [hasWriteAccess, setHasWriteAccess] = useState<boolean>(false);
  const [permissionsLoading, setPermissionsLoading] = useState<boolean>(true);

  // Get email data from provider
  const emailState = getEmail(emailId);
  const { email, loading, error, errorDetails } = emailState;

  // Use custom hooks for form management (only if threadId is provided)
  const {
    isEditMode,
    editBody,
    setEditBody,
    editSubject,
    setEditSubject,
    editTo,
    setEditTo,
    editCc,
    setEditCc,
    editBcc,
    setEditBcc,
    toggleEditMode,
    enableEditMode,
    disableEditMode,
    hasAiEdits,
    acceptAiEdits,
    restoreAiEdits,
  } = useEmailFormManagement(
    email,
    emailId,
    threadId,
    updateThreadViewVisualDescription,
  );

  // Detect fancy email styling when email loads
  useEffect(() => {
    if (email?.htmlBody) {
      setIsFancyEmail(detectFancyEmail(email.htmlBody));
    }
  }, [email?.htmlBody]);

  // Auto-enable edit mode if defaultEditMode is true and email is a draft
  useEffect(() => {
    if (defaultEditMode && email?.isDraft && !isEditMode) {
      enableEditMode();
    }
  }, [defaultEditMode, email?.isDraft, isEditMode, enableEditMode]);

  // Check email permissions
  useEffect(() => {
    const checkPermissions = async () => {
      if (!email || !emailId || !auth0User) {
        setPermissionsLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();

        // Get current user's access level to this email
        const response = await api.get(
          `/agent-email-storage/${emailId}/my-access`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          const { isOwner: owner, permission: perm } = response.data;

          setIsOwner(owner);
          // Owner or WRITE permission grants write access
          setHasWriteAccess(owner || perm === 'WRITE');
        }
      } catch (error: any) {
        // If we get a 403, user has no access
        if (error.response?.status === 403) {
          setIsOwner(false);
          setHasWriteAccess(false);
        } else {
          // For other errors, assume owner with full access
          console.error('Error checking email permissions:', error);
          setIsOwner(true);
          setHasWriteAccess(true);
        }
      } finally {
        setPermissionsLoading(false);
      }
    };

    checkPermissions();
  }, [email, emailId, auth0User, getAccessTokenSilently]);

  // Helper function to convert comma-separated string to array
  const stringToArray = useCallback((str: string): string[] => {
    return str.trim()
      ? str
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0)
      : [];
  }, []);

  // Handle save draft with all editable fields
  const handleSaveDraft = useCallback(
    async (
      editBody: string,
      editSubject?: string,
      editTo?: string,
      editCc?: string,
      editBcc?: string,
    ) => {
      if (!email || !emailId) return;

      // Store original state for rollback
      const originalBody = email.body;
      const originalHtmlBody = email.htmlBody;
      const originalSubject = email.subject;
      const originalTo = email.to;
      const originalCc = email.cc;
      const originalBcc = email.bcc;

      // OPTIMISTIC UPDATE: Update UI immediately via provider
      updateEmailOptimistically(emailId, {
        body: editBody,
        htmlBody: editBody,
        ...(editSubject !== undefined && { subject: editSubject }),
        ...(editTo !== undefined && { to: stringToArray(editTo) }),
        ...(editCc !== undefined && { cc: stringToArray(editCc) }),
        ...(editBcc !== undefined && { bcc: stringToArray(editBcc) }),
      });

      // Exit edit mode optimistically
      if (isEditMode) {
        disableEditMode();
      }

      try {
        const response = await saveDraftEmail(
          emailId,
          {
            content: editBody,
            ...(editSubject !== undefined && { subject: editSubject }),
            ...(editTo !== undefined && { to: editTo }),
            ...(editCc !== undefined && { cc: editCc }),
            ...(editBcc !== undefined && { bcc: editBcc }),
          },
          getAccessTokenSilently,
        );

        if (!response.success) {
          // ROLLBACK: Restore original state via provider
          updateEmailOptimistically(emailId, {
            body: originalBody,
            htmlBody: originalHtmlBody,
            subject: originalSubject,
            to: originalTo,
            cc: originalCc,
            bcc: originalBcc,
          });

          const errorMessage = response.error || 'Failed to save draft';
          const detailedError = response.errorDetails
            ? `${errorMessage}: ${response.errorDetails.message}`
            : errorMessage;
          alert(`Error saving draft: ${detailedError}`);
        } else {
          // Success - call onSave callback if provided
          onSave?.();
        }
      } catch (error: any) {
        // ROLLBACK: Restore original state on network error
        updateEmailOptimistically(emailId, {
          body: originalBody,
          htmlBody: originalHtmlBody,
          subject: originalSubject,
          to: originalTo,
          cc: originalCc,
          bcc: originalBcc,
        });

        console.error('Error saving draft:', error);
        const errorMessage =
          error.response?.data?.error || error.message || 'Unknown error';
        alert(`Failed to save draft: ${errorMessage}`);
      }
    },
    [
      email,
      emailId,
      getAccessTokenSilently,
      updateEmailOptimistically,
      isEditMode,
      disableEditMode,
      stringToArray,
      onSave,
    ],
  );

  // Handle send email
  const handleSendEmail = useCallback(async () => {
    if (!email || !emailId || !email.isDraft) return;

    try {
      setIsSending(true);
      const response = await sendDraftEmail(
        emailId,
        getAccessTokenSilently,
        threadId,
      );

      if (response.success) {
        // Update the email state to reflect it's now sent
        updateEmailOptimistically(emailId, {
          isDraft: false,
          isSent: true,
        });

        // Exit edit mode if we were in it
        if (isEditMode) {
          disableEditMode();
        }

        // Call onSend callback if provided
        onSend?.();
      } else {
        const errorMessage = response.error || 'Failed to send email';
        const detailedError = response.errorDetails
          ? `${errorMessage}: ${response.errorDetails.message}`
          : errorMessage;
        alert(`Error sending email: ${detailedError}`);
      }
    } catch (error: any) {
      console.error('Error sending email:', error);
      const errorMessage =
        error.response?.data?.error || error.message || 'Unknown error';
      alert(`Failed to send email: ${errorMessage}`);
    } finally {
      setIsSending(false);
    }
  }, [
    email,
    emailId,
    threadId,
    isEditMode,
    getAccessTokenSilently,
    disableEditMode,
    updateEmailOptimistically,
    onSend,
  ]);

  // Handle attachment download
  const handleAttachmentClick = useCallback(
    async (attachmentId: string) => {
      if (!emailId) return;

      try {
        await downloadAttachment(emailId, attachmentId, getAccessTokenSilently);
      } catch (err: any) {
        console.error('Error downloading attachment:', err);
        alert(
          `Failed to download attachment: ${err.message || 'Unknown error'}`,
        );
      }
    },
    [emailId, getAccessTokenSilently],
  );

  const onSaveDraft = () => {
    handleSaveDraft(editBody, editSubject, editTo, editCc, editBcc);
  };

  // Wrapper functions to handle exit animations
  const handleAcceptAiEdits = () => {
    setIsExitingAiEdits(true);
    // Wait for animation to complete before calling actual handler
    setTimeout(() => {
      acceptAiEdits();
      setIsExitingAiEdits(false);
    }, 200); // Match the animation duration
  };

  const handleRestoreAiEdits = () => {
    setIsExitingAiEdits(true);
    // Wait for animation to complete before calling actual handler
    setTimeout(() => {
      restoreAiEdits();
      setIsExitingAiEdits(false);
    }, 200); // Match the animation duration
  };

  if (loading) {
    return <div />;
  }

  if (error) {
    // Check if this is a permission denied error from same company
    if (
      errorDetails?.type === 'PERMISSION_DENIED' &&
      errorDetails.sameCompany
    ) {
      return (
        <ErrorState
          title='Permission denied'
          subtitle='The owner must grant permission for you to view this email'
        />
      );
    }

    // Default error message for other errors
    return <ErrorState title='Error' subtitle={`Error: ${error}`} />;
  }

  if (!email) {
    return (
      <ErrorState
        title='Email not found'
        subtitle='The email you are trying to view does not exist'
      />
    );
  }

  // Format date if available
  const formattedDate = email.date
    ? new Date(email.date).toLocaleString()
    : null;

  if (isInboxView) {
    return (
      <InboxViewComponentView
        title={`${email.subject || 'No subject'} - ${email.body?.substring(0, 500) || ''}`}
        onClick={() => {
          if (onInboxViewComponentViewClick && email.id) {
            onInboxViewComponentViewClick(email.id);
          }
        }}
      />
    );
  }

  return (
    <AIReportingWrapper
      breakdown={{
        name: 'Email',
        id: emailId,
        inEditMode: isEditMode,
      }}
    >
      <div
        className={`email-component ${getEmailTypeClass(!!email.isDraft, !!email.isSent)} ${hasAiEdits ? 'has-ai-edits' : ''} ${isExitingAiEdits ? 'exiting' : ''}`}
      >
        {hasAiEdits && (
          <GenericAIComponentChange
            onAcceptState={handleAcceptAiEdits}
            onRestoreState={handleRestoreAiEdits}
          />
        )}
        <div className='email-header'>
          <div className='header-content'>
            {/* Regular header display */}
            <div className='header-primary'>
              {onCollapse && (
                <button className='email-collapse-button' onClick={onCollapse}>
                  <UpCaretIcon />
                </button>
              )}
              <strong>{email.isSent || email.isDraft ? 'From:' : 'To:'}</strong>
              <span>
                {email.isSent || email.isDraft
                  ? email.from
                  : Array.isArray(email.to)
                    ? email.to.join(', ')
                    : email.to}
                <span className='desktop-date'>
                  {formattedDate && ` on ${formattedDate}`}
                </span>
              </span>
            </div>
          </div>
          <div
            className={`email-tag ${email.isDraft ? 'draft' : email.isSent ? 'sent' : 'inbox'}`}
          >
            {email.isDraft ? 'Draft' : email.isSent ? 'Sent' : 'Inbox'}
          </div>
        </div>

        <div className='email-content'>
          {/* Subject - editable in edit mode for drafts */}
          {isEditMode && email.isDraft ? (
            <div className='email-subject-field'>
              <input
                type='text'
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value)}
                className='email-field-input email-subject-input'
                placeholder='Subject...'
              />
            </div>
          ) : (
            <h3 className='email-subject'>{email.subject || '(No Subject)'}</h3>
          )}

          <EmailMetadata
            email={email}
            isEditMode={isEditMode}
            localEmailId={emailId}
            editTo={editTo}
            editCc={editCc}
            editBcc={editBcc}
            setEditTo={setEditTo}
            setEditCc={setEditCc}
            setEditBcc={setEditBcc}
          />

          {isEditMode ? (
            <div className='email-edit-content'>
              <div className='email-edit-inner-wrapper'>
                <WysiwygEditor
                  value={editBody}
                  onChange={setEditBody}
                  placeholder='Email body...'
                  className='email-edit-wysiwyg'
                />
              </div>
            </div>
          ) : (
            <div className='email-body-content'>
              <div
                className={`email-body-shadow ${isFancyEmail ? 'fancy-email' : ''}`}
              >
                {email.htmlBody ? (
                  <IframeWithDynamicHeight htmlContent={email.htmlBody} />
                ) : (
                  <div>{email.body}</div>
                )}
              </div>
            </div>
          )}

          {email.attachments && email.attachments.length > 0 && (
            <div className='email-attachments'>
              <div className='attachments-list'>
                {email.attachments.map((attachment: any, index: number) => (
                  <AttachmentItem
                    key={
                      typeof attachment === 'string'
                        ? index
                        : attachment.id || index
                    }
                    attachment={attachment}
                    index={index}
                    onAttachmentClick={handleAttachmentClick}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Show email actions: for drafts with write access, or for owners to share */}
          {!permissionsLoading &&
            ((email.isDraft && hasWriteAccess) || isOwner) && (
              <EmailActions
                isEditMode={isEditMode}
                isSending={isSending}
                onToggleEdit={toggleEditMode}
                onSaveDraft={onSaveDraft}
                onSendEmail={handleSendEmail}
                hideSubmitButton={removeDedicatedSubmitButton}
                showShareButton={isOwner && !isConversationView}
                onShare={() => setShowShareModal(!showShareModal)}
                isDraft={email.isDraft}
              />
            )}

          {/* Email share modal - inline below actions */}
          {showShareModal && (
            <EmailShareModal
              emailId={emailId}
              isOwner={isOwner}
              onClose={() => setShowShareModal(false)}
            />
          )}
        </div>
      </div>
    </AIReportingWrapper>
  );
};

export default EmailComponent;
